import { Decimal } from 'decimal.js';
import { roundToCents } from '../../accounts-receivable/round-to-cents.js';
import { SalesOrderItemType } from '../../constants/sales-order-item-type.js';
import { roundTaxPercent } from '../../sales-order-totals/round-tax-percent.js';
import { TAX_PERCENT_DECIMALS } from '../../sales-order-totals/tax-percent-decimals.js';
import { isAdjustmentLineType } from '../adjustment-line-types.js';
function totalOf(lines) {
    return lines.reduce((sum, line) => sum.plus(line.totalPrice ?? 0), new Decimal(0));
}
const ofType = (lines, typeId) => lines.filter((line) => line.typeId === typeId);
/** The Tax line's percent of the product subtotal at the 4 places updateSalesOrder accepts, or null when it has none in 0-100. */
function derivedTaxPercent(lines) {
    const productSubtotal = totalOf(lines.filter((line) => line.typeId == null || !isAdjustmentLineType(line.typeId)));
    if (!productSubtotal.greaterThan(0))
        return null;
    const percent = totalOf(ofType(lines, SalesOrderItemType.TAX))
        .dividedBy(productSubtotal)
        .times(100)
        .toDecimalPlaces(TAX_PERCENT_DECIMALS);
    return percent.lessThan(0) || percent.greaterThan(100) ? null : percent.toNumber();
}
function storedTaxes(lines, headerTaxRate) {
    const taxLines = ofType(lines, SalesOrderItemType.TAX);
    // An older single Tax line stores no percent: the header's, else the one its dollars imply.
    const fallback = taxLines.length === 1 ? (headerTaxRate ?? derivedTaxPercent(lines)) : null;
    const taxes = taxLines.map((line) => ({ line, percent: line.taxRate ?? fallback }));
    if (taxes.some(({ percent }) => percent === null))
        return null;
    return taxes.map(({ line, percent }) => ({
        taxRateId: line.taxId,
        percent: roundTaxPercent(percent ?? 0),
        name: line.name,
    }));
}
function storedDiscount(lines) {
    const discountLines = ofType(lines, SalesOrderItemType.DISCOUNT);
    const [line] = discountLines;
    if (!line)
        return null;
    if (line.adjustPercentage != null) {
        return { amount: Number(String(line.adjustPercentage)), discountType: 'percentage' };
    }
    // An older line stores only the dollars taken off, which read back as flat and unrounded.
    const amount = line.adjustAmount != null
        ? Number(String(line.adjustAmount))
        : totalOf(discountLines).abs().toNumber();
    return { amount, discountType: 'flat' };
}
/** The one reading of an order's adjustment lines, shared by the Edit Order sheet and updateSalesOrder's stale check (#336 B7). */
export function storedSalesOrderAdjustments(lines, headerTaxRate) {
    return {
        shippingAmount: roundToCents(totalOf(ofType(lines, SalesOrderItemType.SHIPPING))).toNumber(),
        taxes: storedTaxes(lines, headerTaxRate),
        discount: storedDiscount(lines),
    };
}
//# sourceMappingURL=stored-sales-order-adjustments.js.map